'use client';

import { c as _c } from "react/compiler-runtime";
import { WithLoadingContainer } from '@/comps/loading-spinner';
import { Button } from '@/comps/primitives/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/comps/primitives/command';
import { Dialog, DialogClose, DialogContentWrapper } from '@/comps/primitives/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { useListTags } from '@/api/generated/taxonomy/taxonomy';
import { useUserDimensionMapping } from '@/domains/pages/shared/use-user-dimension-mapping';
import { getSegmentEmptyCopy } from '@/domains/pages/ai-proficiency/utils/segment-empty-state';
import type { SegmentKind } from '@/domains/pages/shared/segment-kind';
import { NEBULY_PAGE_QUERY_OPTIONS } from '@/domains/pages/shared/page-query-options';
import { COHORT_USER_GROUP_DIALOG_CLASS, CohortUserGroupDialogBody, CohortUserGroupDialogFooter, CohortUserGroupDialogHeader } from '@/domains/live-activity/cohorts/components/CohortUserGroupDialogShell';
import { clean } from '@/domains/settings/components/taxonomy/_helpers';
import { useAuthContext } from '@/hooks/use-auth';
import { removeTagPrefix } from '@/models/common/filters';
import { Loader2Icon, Tags, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '@/utils/shad';
type UserDimensionMappingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  segmentKind: SegmentKind;
  projectId: string | undefined;
};
function normalizeTagValue(value: string): string {
  return removeTagPrefix(value);
}
export function UserDimensionMappingDialog(t0) {
  const $ = _c(74);
  const {
    open,
    onOpenChange,
    segmentKind,
    projectId
  } = t0;
  const {
    isCompanyAdmin,
    isNebulyAdmin
  } = useAuthContext();
  const isAdmin = isCompanyAdmin || isNebulyAdmin;
  const shouldOpen = open && isAdmin;
  let t1;
  if ($[0] !== segmentKind) {
    t1 = getSegmentEmptyCopy(segmentKind);
    $[0] = segmentKind;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copy = t1;
  const t2 = shouldOpen && !!projectId;
  let t3;
  if ($[2] !== t2) {
    t3 = {
      query: {
        enabled: t2,
        ...NEBULY_PAGE_QUERY_OPTIONS
      }
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: tagsData,
    isLoading: isLoadingTags
  } = useListTags(projectId, undefined, t3);
  const {
    mappedTagKeys,
    hasMapping,
    isLoading: isLoadingMapping,
    isMutating,
    saveMapping,
    removeMapping
  } = useUserDimensionMapping(segmentKind, shouldOpen);
  const [selectedTag, setSelectedTag] = useState();
  const [pickerOpen, setPickerOpen] = useState(false);
  tagsData?.tags;
  let t4;
  if ($[4] !== tagsData?.tags) {
    t4 = tagsData?.tags ?? [];
    $[4] = tagsData?.tags;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = t4.map(_temp);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const tagOptions = t5;
  const hasTagOptions = tagOptions.length > 0;
  const isContentLoading = isLoadingTags || isLoadingMapping;
  let t6;
  if ($[8] !== selectedTag || $[9] !== tagOptions) {
    t6 = tagOptions.find(option => normalizeTagValue(option.value) === normalizeTagValue(selectedTag ?? ""))?.label;
    $[8] = selectedTag;
    $[9] = tagOptions;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const selectedTagLabel = t6;
  const preferredMappedTag = mappedTagKeys[0] ?? "";
  let t7;
  if ($[11] !== preferredMappedTag) {
    t7 = normalizeTagValue(preferredMappedTag);
    $[11] = preferredMappedTag;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const normalizedPreferredMappedTag = t7;
  let t8;
  if ($[13] !== hasTagOptions || $[14] !== normalizedPreferredMappedTag || $[15] !== preferredMappedTag || $[16] !== shouldOpen || $[17] !== tagOptions) {
    t8 = () => {
      if (!shouldOpen) {
        setPickerOpen(false);
        return;
      }
      if (!hasTagOptions) {
        setSelectedTag(undefined);
        return;
      }
      const preferredOption = tagOptions.find(option_0 => normalizeTagValue(option_0.value) === normalizedPreferredMappedTag);
      if (preferredOption) {
        setSelectedTag(preferredMappedTag || preferredOption.value);
        return;
      }
      setSelectedTag(tagOptions[0]?.value);
    };
    $[13] = hasTagOptions;
    $[14] = normalizedPreferredMappedTag;
    $[15] = preferredMappedTag;
    $[16] = shouldOpen;
    $[17] = tagOptions;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== hasTagOptions || $[20] !== normalizedPreferredMappedTag || $[21] !== shouldOpen || $[22] !== tagOptions) {
    t9 = [hasTagOptions, normalizedPreferredMappedTag, shouldOpen, tagOptions];
    $[19] = hasTagOptions;
    $[20] = normalizedPreferredMappedTag;
    $[21] = shouldOpen;
    $[22] = tagOptions;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  useEffect(t8, t9);
  let t10;
  if ($[24] !== normalizedPreferredMappedTag || $[25] !== onOpenChange || $[26] !== preferredMappedTag || $[27] !== saveMapping || $[28] !== selectedTag) {
    t10 = async () => {
      const tagToSave = selectedTag && preferredMappedTag && normalizeTagValue(selectedTag) === normalizedPreferredMappedTag ? preferredMappedTag : selectedTag;
      try {
        await saveMapping(tagToSave);
        onOpenChange(false);
      } catch {}
    };
    $[24] = normalizedPreferredMappedTag;
    $[25] = onOpenChange;
    $[26] = preferredMappedTag;
    $[27] = saveMapping;
    $[28] = selectedTag;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const handleSave = t10;
  let t11;
  if ($[30] !== onOpenChange || $[31] !== removeMapping) {
    t11 = async () => {
      try {
        await removeMapping();
        onOpenChange(false);
      } catch {}
    };
    $[30] = onOpenChange;
    $[31] = removeMapping;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const handleRemove = t11;
  const isBusy = isMutating || isContentLoading;
  if (!isAdmin) {
    return null;
  }
  let t12;
  if ($[33] !== copy.mappingDialogDescription || $[34] !== copy.mappingDialogTitle) {
    t12 = <CohortUserGroupDialogHeader icon={Tags} title={copy.mappingDialogTitle} description={copy.mappingDialogDescription} />;
    $[33] = copy.mappingDialogDescription;
    $[34] = copy.mappingDialogTitle;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== copy.mappingNoTagsMessage || $[37] !== hasTagOptions || $[38] !== pickerOpen || $[39] !== selectedTagLabel || $[40] !== tagOptions) {
    t13 = hasTagOptions ? <Popover open={pickerOpen} onOpenChange={setPickerOpen} modal={true}><PopoverTrigger asChild={true}><Button type="button" variant="outline" role="combobox" aria-expanded={pickerOpen} className={cn("h-8 w-full max-w-full justify-between rounded-xl px-2 py-1.5 text-sm font-normal", !selectedTagLabel && "text-muted-foreground")}><span className="truncate">{selectedTagLabel ?? "Select a tag"}</span><ChevronDown className="ml-2 size-4 shrink-0 opacity-50" /></Button></PopoverTrigger><PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start"><Command><CommandInput placeholder="Search tags..." /><CommandList className="max-h-[min(16rem,50vh)]"><CommandEmpty>No tags found.</CommandEmpty><CommandGroup>{tagOptions.map(option_1 => <CommandItem key={option_1.value} value={`${option_1.label} ${option_1.value}`} onSelect={() => {
                setSelectedTag(option_1.value);
                setPickerOpen(false);
              }}>{option_1.label}</CommandItem>)}</CommandGroup></CommandList></Command></PopoverContent></Popover> : <div className="rounded-lg border border-border bg-muted/20 px-4 py-3 text-sm leading-relaxed text-muted-foreground">{copy.mappingNoTagsMessage}</div>;
    $[36] = copy.mappingNoTagsMessage;
    $[37] = hasTagOptions;
    $[38] = pickerOpen;
    $[39] = selectedTagLabel;
    $[40] = tagOptions;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== isContentLoading || $[43] !== t13) {
    t14 = <CohortUserGroupDialogBody><WithLoadingContainer hideChildren={true} loading={isContentLoading} className="pb-0">{t13}</WithLoadingContainer></CohortUserGroupDialogBody>;
    $[42] = isContentLoading;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  let t15;
  if ($[45] !== handleRemove || $[46] !== hasMapping || $[47] !== isBusy) {
    t15 = hasMapping ? <Button variant="outline" type="button" className="text-destructive hover:text-destructive" onClick={handleRemove} disabled={isBusy}>Remove mapping</Button> : <span />;
    $[45] = handleRemove;
    $[46] = hasMapping;
    $[47] = isBusy;
    $[48] = t15;
  } else {
    t15 = $[48];
  }
  let t16;
  if ($[49] !== onOpenChange) {
    t16 = () => onOpenChange(false);
    $[49] = onOpenChange;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  let t17;
  if ($[51] !== isBusy || $[52] !== t16) {
    t17 = <DialogClose asChild={true}><Button type="button" variant="outline" onClick={t16} disabled={isBusy}>Cancel</Button></DialogClose>;
    $[51] = isBusy;
    $[52] = t16;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  const t18 = !selectedTag || !hasTagOptions || isBusy;
  let t19;
  if ($[54] !== isBusy) {
    t19 = isBusy ? <Loader2Icon className="mr-2 size-4 animate-spin" /> : null;
    $[54] = isBusy;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== handleSave || $[57] !== t18 || $[58] !== t19) {
    t20 = <Button type="button" onClick={handleSave} disabled={t18}>{t19}Save mapping</Button>;
    $[56] = handleSave;
    $[57] = t18;
    $[58] = t19;
    $[59] = t20;
  } else {
    t20 = $[59];
  }
  let t21;
  if ($[60] !== t17 || $[61] !== t20) {
    t21 = <div className="flex items-center gap-2">{t17}{t20}</div>;
    $[60] = t17;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  let t22;
  if ($[63] !== t15 || $[64] !== t21) {
    t22 = <CohortUserGroupDialogFooter><div className="flex w-full items-center justify-between gap-2">{t15}{t21}</div></CohortUserGroupDialogFooter>;
    $[63] = t15;
    $[64] = t21;
    $[65] = t22;
  } else {
    t22 = $[65];
  }
  let t23;
  if ($[66] !== t12 || $[67] !== t14 || $[68] !== t22) {
    t23 = <DialogContentWrapper className={COHORT_USER_GROUP_DIALOG_CLASS}>{t12}{t14}{t22}</DialogContentWrapper>;
    $[66] = t12;
    $[67] = t14;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== onOpenChange || $[71] !== shouldOpen || $[72] !== t23) {
    t24 = <Dialog open={shouldOpen} onOpenChange={onOpenChange}>{t23}</Dialog>;
    $[70] = onOpenChange;
    $[71] = shouldOpen;
    $[72] = t23;
    $[73] = t24;
  } else {
    t24 = $[73];
  }
  return t24;
}
function _temp(tag) {
  return {
    label: clean(tag.label),
    value: tag.value
  };
}